/**
 * Presence of the ACE ops people who service this fund, as shown in the portal
 * header. The three states mirror what Microsoft Teams reports for a user,
 * collapsed to what a GP actually needs to know:
 *
 *   active — reachable right now
 *   away   — logged in but idle / in a meeting (Teams: Away, Be right back, Busy, DND)
 *   off    — outside office hours or offline (Teams: Offline, Out of office)
 *
 * The backend will do the Teams -> OpsStatus collapsing so the portal never has
 * to know Teams' full availability vocabulary.
 */
export type OpsStatus = 'active' | 'away' | 'off';

/** Office hours as a display string plus the timezone they're stated in. */
export interface OpsOfficeHours {
  /** e.g. 'Mon–Fri, 09:00–18:00' */
  days: string;
  /** IANA-ish label shown to the user, e.g. 'CET' */
  timezone: string;
}

export interface OpsContact {
  id: string;
  name: string;
  /** Work address — also the join key to Teams presence on the backend side. */
  email: string;
  /** What they do for this fund, e.g. 'Fund Accountant'. */
  role: string;
  status: OpsStatus;
  /**
   * Optional free-text qualifier the backend can pass through from Teams
   * (out-of-office note, "back at 14:00"). Shown under the status when present.
   */
  statusNote?: string;
  officeHours: OpsOfficeHours;
  avatarUrl?: string;
}

/** Labels + ordering shared by the bubble tooltip and the panel. */
export const OPS_STATUS_LABELS: Record<OpsStatus, string> = {
  active: 'Active',
  away: 'Away',
  off: 'Off',
};
